import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  Divider,
  Tab,
  Tabs,
  CircularProgress,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination,
  Snackbar,
  Tooltip,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  AlertTitle,
} from '@mui/material';
import {
  ArrowForward,
  ArrowBack,
  Face as FaceIcon,
  PersonAdd as PersonAddIcon,
  PlayArrow as RunIcon,
  Stop as StopIcon,
  CheckCircle,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  SkipNext as SkipIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
} from '@mui/icons-material';
import faceRecognitionService from '../../services/faceRecognitionService';
import imageService from '../../api/imageService';
import { io as socketIOClient } from 'socket.io-client';
import sessionService from '../../api/sessionService';
import { getServerUrl } from '../../config/runtimeConfig';
import ReferencePersonsList from '../ReferencePersonsList';
import ReferencePersonDialog from '../ReferencePersonDialog';
import AuthenticatedImage from '../AuthenticatedImage';
import AuthenticatedAvatar from '../AuthenticatedAvatar';

const API_URL = getServerUrl();

/**
 * WizardStep2FaceRecognition
 * Props:
 *   session       – current session object
 *   onNext        – () => void  – advance wizard
 *   onBack        – () => void  – go back
 */
// Format milliseconds into a readable string
const formatTime = (ms) => {
  if (!ms || ms <= 0) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const WizardStep2FaceRecognition = ({ session, onNext, onBack, onNavConfigChange }) => {
  const sessionId = session?.uniqueId;

  // ── Tabs ─────────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState(0);

  // ── Reference persons ─────────────────────────────────────────────────────────
  const [referencePersons, setReferencePersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ── Tag-person image grid ─────────────────────────────────────────────────────
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imgPage, setImgPage] = useState(0);
  const [imgRowsPerPage, setImgRowsPerPage] = useState(12);
  const [imgTotal, setImgTotal] = useState(0);
  const [referenceDialog, setReferenceDialog] = useState({ open: false, image: null });

  // ── Subset image selection (Find Faces tab) ───────────────────────────────────
  const [allSessionImages, setAllSessionImages] = useState([]); // for selection panel
  const [selPage, setSelPage] = useState(0);
  const [selRowsPerPage, setSelRowsPerPage] = useState(12);
  const [selTotal, setSelTotal] = useState(0);
  const [selLoading, setSelLoading] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]); // empty = all images
  const [useSubset, setUseSubset] = useState(false);

  // ── Session image stats ───────────────────────────────────────────────────────
  const [imageStats, setImageStats] = useState(null);    // { total, alreadyProcessed, newlyProcessed } — last run
  const [sessionStats, setSessionStats] = useState(null); // { total, processed, unprocessed } — from backend on mount

  // ── AWS run ───────────────────────────────────────────────────────────────────
  const [running, setRunning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [stuckOperation, setStuckOperation] = useState(false); // 409 — previous run still flagged in progress
  const [runProgress, setRunProgress] = useState(null); // { percentage, current, total, status, isCompleted, hasError }
  // Always-current ref so handleCancelRecognition can read the latest % synchronously
  const runProgressPctRef = useRef(0);
  const [runResult, setRunResult] = useState(null);
  const [runOptions, setRunOptions] = useState({
    mainFacesOnly: true,
    overrideProcessed: false,
    showAllFaces: false,
  });

  // Track whether the WebSocket sent a completed/error event.
  // After the HTTP POST resolves, if this is still false, we treat the HTTP result as the
  // final result (covers EARLY EXIT, lost WS messages, etc.)
  const wsCompletedRef = useRef(false);
  const otherTabSocketRef = useRef(null);
  // Snapshot of the percentage when the user clicks Cancel.
  // Once set (non-null), progress bar is frozen — stale in-flight progress events
  // from the backend (images still being processed before the cancel flag is checked)
  // should not advance the bar any further.
  const cancelledAtPercentageRef = useRef(null);

  // ── Other-tab run detection ───────────────────────────────────────────────────
  const [otherTabRunning, setOtherTabRunning] = useState(false);

  // ── Matches accordion ─────────────────────────────────────────────────────────
  const [personMatches, setPersonMatches] = useState({});
  const [loadingMatches, setLoadingMatches] = useState({});

  // ── Snackbar ──────────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch session-level face recognition stats (processed vs total)
  // ─────────────────────────────────────────────────────────────────────────────
  const loadSessionStats = useCallback(async () => {
    if (!sessionId) return;
    try {
      const resp = await imageService.getAwsFaceRecognitionStatus(sessionId);
      if (resp?.imageStats) setSessionStats(resp.imageStats);
    } catch (err) {
      console.warn('[Step2] Could not load session stats', err);
    }
  }, [sessionId]);

  useEffect(() => { loadSessionStats(); }, [loadSessionStats]);

  // Check once on mount whether another tab/session has recognition running.
  // If so, join the Socket.IO room to receive live progress events.
  useEffect(() => {
    if (!sessionId) return;
    imageService.getAwsFaceRecognitionStatus(sessionId)
      .then((resp) => {
        const opStatus = resp?.operationStatus;
        const inProgress = opStatus?.inProgress;
        const isStale    = opStatus?.isStale;

        if (inProgress) {
          // The job is running elsewhere (another tab, server-resumed, etc.).
          // Always show the top-level "running" banner by setting otherTabRunning=true
          // but NOT running=true — running=true is reserved for runs started in this tab.
          // The banner condition is {otherTabRunning && !running}, so we must NOT set running.
          setOtherTabRunning(true);

          // Seed progress display from DB-persisted data (updated every photo by the backend)
          const pct = opStatus.current && opStatus.total
            ? Math.round((opStatus.current / opStatus.total) * 100)
            : (opStatus.progressData?.percentage ?? 0);
          setRunProgress({
            percentage: pct,
            current: opStatus.current || 0,
            total:   opStatus.total   || 0,
            status: isStale
              ? 'Recognition running — no recent progress updates (may be slow or stuck)…'
              : 'Recognition running elsewhere…',
            currentPhotoName: opStatus.progressData?.currentPhotoName || null,
            lastImageTime:           opStatus.progressData?.lastImageTime || 0,
            averageTime:             opStatus.progressData?.averageTime || 0,
            estimatedTimeRemaining:  opStatus.progressData?.estimatedTimeRemaining || 0,
            isCompleted: false,
            hasError: false,
          });

          if (isStale) {
            // Mark as potentially stuck so the "Force Cancel" option is available in Tab 2.
            // If a live Socket.IO progress event arrives it will clear this flag automatically.
            setStuckOperation(true);
          }

          // Join Socket.IO room so this tab gets live progress and completion events.
          const socket = socketIOClient(API_URL, { path: '/socket.io/', withCredentials: true, transports: ['polling'] });
          otherTabSocketRef.current = socket;
          socket.on('connect', () => socket.emit('joinFaceRecognitionRoom', { sessionId }));
          socket.on('faceRecognitionProgress', (data) => {
            if (data.sessionId !== sessionId) return;
            // First live event confirms the job is genuinely alive — clear the stuck warning.
            setStuckOperation(false);
            handleProgressData({ ...data, isCompleted: false, hasError: false });
          });
          socket.on('faceRecognitionComplete', (data) => {
            if (data.sessionId !== sessionId) return;
            handleProgressData({ ...data });
            socket.disconnect();
            otherTabSocketRef.current = null;
          });
        } else if (isStale) {
          // inProgress=false but status='interrupted' — server startup cleaned it up.
          // The job is definitely not running, just leftover DB state.
          setStuckOperation(true);
        }
        if (resp?.imageStats) setSessionStats(resp.imageStats);
      })
      .catch(() => {});
    return () => {
      if (otherTabSocketRef.current) {
        otherTabSocketRef.current.disconnect();
        otherTabSocketRef.current = null;
      }
    };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // Load reference persons
  // ─────────────────────────────────────────────────────────────────────────────
  const loadReferencePersons = useCallback(async () => {
    if (!sessionId) return;
    setLoadingPersons(true);
    try {
      const data = await faceRecognitionService.getSessionReferencePersons(sessionId);
      setReferencePersons(data.referencePersons || []);
    } catch (err) {
      console.error('[Step2] Failed to load reference persons', err);
    } finally {
      setLoadingPersons(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadReferencePersons();
  }, [loadReferencePersons, refreshTrigger]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Load images for tagging grid
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab !== 1 || !sessionId) return;
    const load = async () => {
      setLoadingImages(true);
      try {
        const resp = await sessionService.getSessionImages(sessionId, {
          page: imgPage + 1,
          limit: imgRowsPerPage,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          mediaType: 'image',
        });
        setImages(resp.images || []);
        setImgTotal(resp.pagination?.totalCount || resp.pagination?.total || 0);
      } catch (err) {
        console.error('[Step2] Failed to load images', err);
      } finally {
        setLoadingImages(false);
      }
    };
    load();
  }, [sessionId, tab, imgPage, imgRowsPerPage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Load images for Find Faces subset selection panel
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab !== 2 || !sessionId) return;
    const load = async () => {
      setSelLoading(true);
      try {
        const resp = await sessionService.getSessionImages(sessionId, {
          page: selPage + 1,
          limit: selRowsPerPage,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          mediaType: 'image',
        });
        setAllSessionImages(resp.images || []);
        setSelTotal(resp.pagination?.totalCount || resp.pagination?.total || 0);
      } catch (err) {
        console.error('[Step2] Failed to load images for selection', err);
      } finally {
        setSelLoading(false);
      }
    };
    load();
  }, [sessionId, tab, selPage, selRowsPerPage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Shared progress/completion handler — used by BOTH the main run socket and the
  // other-tab on-mount socket so both get identical freeze/cancel logic.
  // ─────────────────────────────────────────────────────────────────────────────
  const handleProgressData = useCallback((data) => {
    const wasCancelled = data.cancelled || data.results?.cancelled || false;
    const frozenPct = cancelledAtPercentageRef.current; // null = cancel not yet requested (or other tab)
    // When the complete event arrives with wasCancelled, use backend's authoritative %
    // so both tabs converge on the same value (own-tab was frozen at click-time, other-tab never had a snapshot).
    const completedCancelledPct = (data.isCompleted && wasCancelled && data.percentage != null)
      ? data.percentage : null;
    const cancelledAtPct = wasCancelled
      ? (completedCancelledPct ?? frozenPct ?? data.percentage ?? null)
      : null;
    const nextPercentage = (data.isCompleted && !wasCancelled) ? 100
      : (completedCancelledPct !== null) ? completedCancelledPct  // complete event: backend-authoritative %
      : (frozenPct !== null) ? frozenPct                          // own tab stale progress: stay frozen
      : (data.percentage ?? null);                                 // normal progress
    if (frozenPct === null && nextPercentage !== null && !wasCancelled) runProgressPctRef.current = nextPercentage;
    // Actual photos processed (used when the complete event arrives for a cancellation).
    // For stale in-flight progress events on own tab (frozenPct !== null), freeze counter at prev value
    // so it doesn't advance to totalCount while the backend drains remaining images.
    const completedCancelledCurrent = (data.isCompleted && wasCancelled)
      ? ((data.results?.processedImages ?? 0) + (data.results?.alreadyProcessedCount ?? 0)) || null
      : null;
    setRunProgress((prev) => ({
      ...(prev || {}),
      ...data,
      current: completedCancelledCurrent !== null
        ? completedCancelledCurrent
        : (frozenPct !== null)
          ? (prev?.current ?? 0)  // own tab: freeze counter while stale events drain
          : (data.currentIndex ?? data.current ?? prev?.current ?? 0),
      total:   data.totalCount  ?? data.total  ?? prev?.total  ?? 0,
      percentage: nextPercentage ?? prev?.percentage ?? 0,
      status: wasCancelled
        ? `Cancelled after ${cancelledAtPct ?? prev?.percentage ?? 0}%`
        : data.status,
      isCancelled: wasCancelled,
    }));
    if (data.isCompleted && !data.hasError && !wasCancelled) {
      wsCompletedRef.current = true;
      setRunning(false);
      setCancelling(false);
      setOtherTabRunning(false);
      setRunResult(data.results);
      loadReferencePersons();
      loadSessionStats();
      if (data.results?.alreadyProcessedCount !== undefined || data.results?.processedImages !== undefined) {
        setImageStats({
          total: (data.results?.processedImages || 0) + (data.results?.alreadyProcessedCount || 0),
          alreadyProcessed: data.results?.alreadyProcessedCount || 0,
          newlyProcessed: data.results?.processedImages || 0,
        });
      }
      const uniquePersons = [...new Set((data.results?.matches || []).map((m) => m.personName))];
      showSnackbar(
        uniquePersons.length > 0
          ? `Recognition complete — found ${uniquePersons.length} person(s): ${uniquePersons.join(', ')}`
          : 'Recognition complete — no new matches found.',
        uniquePersons.length > 0 ? 'success' : 'info'
      );
    }
    if (data.isCompleted && wasCancelled) {
      wsCompletedRef.current = true;
      setRunning(false);
      setCancelling(false);
      setOtherTabRunning(false);
      showSnackbar('Recognition cancelled.', 'warning');
    }
    if (data.hasError) {
      wsCompletedRef.current = true;
      setRunning(false);
      setCancelling(false);
      setOtherTabRunning(false);
      showSnackbar('Recognition failed: ' + (data.error || 'Unknown error'), 'error');
    }
  }, [loadReferencePersons, loadSessionStats]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // Run AWS face recognition across session (or subset)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleRunRecognition = async () => {
    if (!sessionId) return;
    if (referencePersons.length === 0) {
      showSnackbar('Please tag at least one reference person before running recognition.', 'warning');
      return;
    }

    wsCompletedRef.current = false;
    cancelledAtPercentageRef.current = null;
    runProgressPctRef.current = 0;
    setStuckOperation(false);
    setOtherTabRunning(false);
    setRunning(true);
    setCancelling(false);
    setRunResult(null);
    setRunProgress({
      percentage: 0, current: 0, total: 0,
      status: 'Initialising…', isCompleted: false, hasError: false,
    });

    const targetImageIds = useSubset && selectedImageIds.length > 0 ? selectedImageIds : null;

    try {
      const result = await imageService.findPersonMatchesWithAws(
        sessionId,
        handleProgressData,
        targetImageIds,
        {
          mainFacesOnly: runOptions.mainFacesOnly,
          overrideProcessed: runOptions.overrideProcessed,
          showAllFaces: runOptions.showAllFaces,
        }
      );

      // ── Completion fallback ────────────────────────────────────────────────
      // If the backend responded with { started: true }, the job is running in the
      // background — the WebSocket will deliver the real completion event shortly.
      // If the WebSocket ALREADY delivered completion (EARLY EXIT etc.), nothing to do.
      if (!wsCompletedRef.current) {
        if (result?.started === true) {
          // Fire-and-forget acknowledged — keep running state, wait for WebSocket
          return;
        }
        setRunning(false);
        setCancelling(false);
        if (result?.success) {
          const alreadyCount = result.alreadyProcessedCount || result.skippedDueToProcessing || 0;
          const newlyCount   = result.processedImages || result.awsMarkedCount || 0;
          const totalCount   = alreadyCount + newlyCount;
          const matches      = result.matches || [];
          const msg = result.message
            || (alreadyCount > 0 && newlyCount === 0
                ? `All ${alreadyCount} image(s) were already processed. Enable "Re-process" to run again.`
                : `Recognition complete — ${newlyCount} image(s) processed.`);

          setRunProgress({
            percentage: 100,
            current: totalCount,
            total: totalCount,
            status: msg,
            isCompleted: true,
            hasError: false,
            results: result,
          });
          setRunResult(result);
          setImageStats({ total: totalCount, alreadyProcessed: alreadyCount, newlyProcessed: newlyCount });
          loadReferencePersons();
          loadSessionStats();

          const uniquePersons = [...new Set(matches.map((m) => m.personName))];
          showSnackbar(
            uniquePersons.length > 0
              ? `Recognition complete — found ${uniquePersons.length} person(s): ${uniquePersons.join(', ')}`
              : msg,
            alreadyCount > 0 && newlyCount === 0 ? 'info' : 'success'
          );
        } else {
          setRunProgress((prev) => ({
            ...(prev || {}),
            status: 'Run completed.',
            isCompleted: true,
            percentage: 100,
          }));
        }
      }
    } catch (err) {
      setRunning(false);
      setCancelling(false);
      if (err.status === 409) {
        // Backend says a run is actively in progress (another tab or a stale flag).
        // Show the cross-tab banner so they can cancel it first.
        setOtherTabRunning(true);
        setRunProgress(null);
      } else {
        showSnackbar('Recognition failed: ' + err.message, 'error');
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Force-cancel a stuck / lingering operation, then retry
  // ─────────────────────────────────────────────────────────────────────────────
  const handleForceCancelAndRetry = async () => {
    setCancelling(true);
    try {
      await imageService.forceResetAwsFaceRecognition(sessionId);
      setStuckOperation(false);
      setCancelling(false);
      handleRunRecognition();
    } catch (err) {
      setCancelling(false);
      showSnackbar('Failed to clear stuck operation: ' + err.message, 'error');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Cancel AWS recognition
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCancelRecognition = async () => {
    if (!sessionId || cancelling) return;
    // Snapshot current % via the always-current ref (state is not synchronously readable).
    // Setting cancelledAtPercentageRef freezes the bar — any progress events arriving
    // after this point will not advance the percentage display any further.
    cancelledAtPercentageRef.current = runProgressPctRef.current;
    setCancelling(true);
    try {
      await imageService.cancelAwsFaceRecognition(sessionId);
      setOtherTabRunning(false);
      setCancelling(false);
      showSnackbar('Cancellation requested — waiting for the current image to finish…', 'warning');
    } catch (err) {
      showSnackbar('Cancel failed: ' + err.message, 'error');
      setCancelling(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Subset image toggle helpers
  // ─────────────────────────────────────────────────────────────────────────────
  const toggleImageSelection = (id) => {
    setSelectedImageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllOnPage = () => {
    const pageIds = allSessionImages.map((img) => img.uniqueId);
    setSelectedImageIds((prev) => [...new Set([...prev, ...pageIds])]);
  };

  const clearAllOnPage = () => {
    const pageIds = new Set(allSessionImages.map((img) => img.uniqueId));
    setSelectedImageIds((prev) => prev.filter((id) => !pageIds.has(id)));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Matches accordion per person
  // ─────────────────────────────────────────────────────────────────────────────
  const togglePersonMatches = async (personId) => {
    if (personMatches[personId]) {
      // collapse
      setPersonMatches((prev) => { const c = { ...prev }; delete c[personId]; return c; });
      return;
    }
    setLoadingMatches((prev) => ({ ...prev, [personId]: true }));
    try {
      const resp = await faceRecognitionService.findPersonMatches(sessionId, personId);
      setPersonMatches((prev) => ({ ...prev, [personId]: resp.matches || [] }));
    } catch (err) {
      showSnackbar('Failed to load matches: ' + err.message, 'error');
    } finally {
      setLoadingMatches((prev) => ({ ...prev, [personId]: false }));
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived state
  // ─────────────────────────────────────────────────────────────────────────────
  const hasPersons = referencePersons.length > 0;
  const runCompleted = runProgress?.isCompleted && !runProgress?.hasError;
  const allAnalysed = sessionStats?.total > 0 && sessionStats?.unprocessed === 0;
  const canContinue = hasPersons; // can continue even without running — face tagging is sufficient

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onNavConfigChange?.({
      nextLabel: 'Continue',
      nextDisabled: !canContinue,
      backDisabled: running,
      showSkip: true,
      skipLabel: 'Skip',
    });
  }, [canContinue, running]); // onNavConfigChange is stable

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Top navigation ──────────────────────────────────────────────────────── */}
      {/* ── Instructions ── */}
      <Box sx={{ px: 2, py: 1.5, mb: 2, bgcolor: 'grey.50', borderLeft: '3px solid', borderColor: 'primary.main', borderRadius: '0 8px 8px 0' }}>
        <Typography variant="body2" color="text.secondary">
          Tag people in your photos so the album can identify and credit them. Once you have tagged
          at least one person, run <strong>Find Faces</strong> across all session photos. Face
          analysis records where each person appears — enabling precise placement in the album.
        </Typography>
      </Box>

      {/* ── Header row ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Face Recognition
        </Typography>
        <Chip
          icon={<PeopleIcon />}
          label={`${referencePersons.length} reference person${referencePersons.length !== 1 ? 's' : ''}`}
          color={hasPersons ? 'primary' : 'default'}
          variant={hasPersons ? 'filled' : 'outlined'}
        />
        <Tooltip title="Refresh reference persons">
          <IconButton size="small" onClick={() => setRefreshTrigger((t) => t + 1)} disabled={loadingPersons}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Why this matters ────────────────────────────────────────────────── */}
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
        <AlertTitle>Needed for correct album layout</AlertTitle>
        Face analysis records where each person appears in every photo — enabling precise placement
        in the album and accurate person pages. Skipping this step may result in photos being
        misplaced in the layout.
      </Alert>

      {/* ── Mini guided stepper ──────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap' }}>Workflow</Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>
            {/* ── Mini guided stepper ──────────────────────────────────────────────── */}
      <Stepper
        activeStep={Math.min(tab, 2)}
        alternativeLabel
        sx={{ mb: 2, '& .MuiStepLabel-label': { fontSize: '0.78rem' } }}
      >
        <Step completed={hasPersons && tab > 0}>
          <StepLabel>View tagged persons</StepLabel>
        </Step>
        <Step completed={hasPersons}>
          <StepLabel>Tag a person</StepLabel>
        </Step>
        <Step completed={allAnalysed || runCompleted}>
          <StepLabel>Run analysis</StepLabel>
        </Step>
      </Stepper>

      {/* ── Another tab is running recognition ──────────────────────────────── */}
      {otherTabRunning && !running && (
        <Alert
          severity={stuckOperation ? 'warning' : 'info'}
          icon={<CircularProgress size={18} color="inherit" />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="error"
              size="small"
              variant="outlined"
              startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : <StopIcon />}
              onClick={handleCancelRecognition}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          }
        >
          {stuckOperation
            ? <>
                Face recognition is running but has <strong>not sent a progress update recently</strong>.
                It may be processing a slow image or may have stalled. Wait for it to complete, or cancel it
                and start a new run.
              </>
            : <>
                Face recognition is currently running. Wait for it to complete, or cancel it to start a new run here.
              </>
          }
        </Alert>
      )}

      {/* ── Alert when no persons yet ────────────────────────────────────────── */}
      {!hasPersons && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          No reference persons yet. Go to the <strong>Tag Person</strong> tab, pick a photo and tag
          someone. You can also skip this step and proceed without face recognition.
        </Alert>
      )}

      {/* ── Face recognition stats bar ─────────────────────────────────────── */}
      {sessionStats && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            bgcolor: 'action.hover',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FaceIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">Session images:</Typography>
          </Box>
          <Chip size="small" label={`${sessionStats.total} total`} variant="outlined" />
          <Chip
            size="small"
            label={`${sessionStats.processed} analysed`}
            color={sessionStats.processed === sessionStats.total && sessionStats.total > 0 ? 'success' : 'primary'}
            variant={sessionStats.processed > 0 ? 'filled' : 'outlined'}
          />
          <Chip
            size="small"
            label={`${sessionStats.unprocessed} not yet analysed`}
            color={sessionStats.unprocessed > 0 ? 'warning' : 'default'}
            variant={sessionStats.unprocessed > 0 ? 'filled' : 'outlined'}
          />
          <Tooltip title="Refresh stats">
            <IconButton size="small" onClick={loadSessionStats}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Reference Persons" />
          <Tab label="Tag Person" />
          <Tab label="Find Faces" />
          <Tab label="Matches" />
        </Tabs>
      </Box>

      {/* ══ Tab 0 – Reference Persons ══════════════════════════════════════════ */}
      {tab === 0 && (
        <Box>
          <ReferencePersonsList
            sessionId={sessionId}
            refreshTrigger={refreshTrigger}
            persons={referencePersons}
            onDelete={loadReferencePersons}
          />
          {/* Sub-step guidance */}
          <Box
            sx={{
              mt: 3, pt: 2,
              borderTop: '1px solid', borderColor: 'divider',
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', flexWrap: 'wrap', gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {referencePersons.length === 0
                ? 'No persons tagged yet. Go to the next tab to tag someone.'
                : `${referencePersons.length} person(s) tagged. Proceed to run analysis or tag more people.`}
            </Typography>
            <Button
              variant="outlined"
              endIcon={<ArrowForward />}
              onClick={() => setTab(referencePersons.length > 0 ? 2 : 1)}
            >
              {referencePersons.length > 0 ? 'Go to Run Analysis' : 'Tag a Person'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ══ Tab 1 – Tag Person ════════════════════════════════════════════════ */}
      {tab === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Click <strong>Tag Person</strong> on any photo to assign a name to the main face in that
            image. This creates a reference record used during the bulk recognition run.
          </Typography>

          {loadingImages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : images.length === 0 ? (
            <Alert severity="warning">
              No images in this session yet. Please upload photos first (Step 1).
            </Alert>
          ) : (
            <>
              <Grid container spacing={2}>
                {images.map((image) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={image.uniqueId}>
                    <Card>
                      <AuthenticatedImage
                        imageId={image.uniqueId}
                        type="thumbnail"
                        fallbackToServe
                        alt={image.name}
                        sx={{ width: '100%', height: 200, objectFit: 'contain', display: 'block', bgcolor: 'grey.50' }}
                      />
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="body2" noWrap title={image.name}>
                          {image.name}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ pt: 0 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAddIcon />}
                          fullWidth
                          onClick={() => setReferenceDialog({ open: true, image })}
                        >
                          Tag Person
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <TablePagination
                component="div"
                count={imgTotal}
                page={imgPage}
                onPageChange={(_, p) => setImgPage(p)}
                rowsPerPage={imgRowsPerPage}
                onRowsPerPageChange={(e) => { setImgRowsPerPage(parseInt(e.target.value, 10)); setImgPage(0); }}
                rowsPerPageOptions={[12, 24, 48]}
                sx={{ mt: 1 }}
              />
            </>
          )}

          {/* Reference person dialog */}
          {referenceDialog.open && (
            <ReferencePersonDialog
              open={referenceDialog.open}
              onClose={() => setReferenceDialog({ open: false, image: null })}
              sessionId={sessionId}
              image={referenceDialog.image}
              onSuccess={() => {
                setReferenceDialog({ open: false, image: null });
                setRefreshTrigger((t) => t + 1);
                showSnackbar('Reference person added!', 'success');
              }}
            />
          )}
          {/* Sub-step guidance */}
          <Box
            sx={{
              mt: 3, pt: 2,
              borderTop: '1px solid', borderColor: 'divider',
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', flexWrap: 'wrap', gap: 1,
            }}
          >
            <Button variant="text" startIcon={<ArrowBack />} onClick={() => setTab(0)}>
              Back to Persons
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {referencePersons.length === 0
                  ? 'Tag at least one person to proceed.'
                  : `${referencePersons.length} person(s) ready.`}
              </Typography>
              <Button
                variant="outlined"
                endIcon={<ArrowForward />}
                onClick={() => setTab(2)}
                disabled={!hasPersons}
              >
                Run Analysis
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* ══ Tab 2 – Find Faces ════════════════════════════════════════════════ */}
      {tab === 2 && (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Run AWS Rekognition across <strong>all photos in this session</strong> (or a selected
            subset) to find every occurrence of your tagged reference persons.
          </Typography>

          {/* ── Last-run detail (appears after a run completes) ───────────────── */}
          {imageStats && (
            <Alert
              severity={imageStats.newlyProcessed === 0 ? 'info' : 'success'}
              icon={<InfoIcon />}
              sx={{ mb: 2 }}
            >
              <strong>Last run:</strong> {imageStats.total} image(s) total —{' '}
              {imageStats.newlyProcessed} newly processed,{' '}
              {imageStats.alreadyProcessed} already processed (skipped).
              {imageStats.alreadyProcessed > 0 && imageStats.newlyProcessed === 0 && (
                <> Enable <strong>"Re-process"</strong> below to force reprocessing.</>
              )}
            </Alert>
          )}

          {/* ── Options ─────────────────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Options
            </Typography>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={runOptions.mainFacesOnly}
                    onChange={(e) => setRunOptions((o) => ({ ...o, mainFacesOnly: e.target.checked }))}
                    disabled={running}
                  />
                }
                label={
                  <Typography variant="body2">
                    Main faces only (ignore small/background faces)
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={runOptions.showAllFaces}
                    onChange={(e) => setRunOptions((o) => ({ ...o, showAllFaces: e.target.checked }))}
                    disabled={running}
                  />
                }
                label={
                  <Typography variant="body2">
                    Show all detected faces (include unmatched faces in results)
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={runOptions.overrideProcessed}
                    onChange={(e) => setRunOptions((o) => ({ ...o, overrideProcessed: e.target.checked }))}
                    disabled={running}
                  />
                }
                label={
                  <Typography variant="body2">
                    Re-process already processed images
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={useSubset}
                    onChange={(e) => {
                      setUseSubset(e.target.checked);
                      if (!e.target.checked) setSelectedImageIds([]);
                    }}
                    disabled={running}
                  />
                }
                label={
                  <Typography variant="body2">
                    Run on selected images only
                  </Typography>
                }
              />
            </Stack>
          </Paper>

          {/* ── Subset image selection grid ──────────────────────────────────── */}
          {useSubset && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                  Select Images ({selectedImageIds.length} selected)
                </Typography>
                <Button
                  size="small"
                  startIcon={<SelectAllIcon />}
                  onClick={selectAllOnPage}
                  disabled={running}
                >
                  Select page
                </Button>
                <Button
                  size="small"
                  startIcon={<DeselectIcon />}
                  onClick={clearAllOnPage}
                  disabled={running}
                >
                  Deselect page
                </Button>
                <Button
                  size="small"
                  color="warning"
                  onClick={() => setSelectedImageIds([])}
                  disabled={running || selectedImageIds.length === 0}
                >
                  Clear all
                </Button>
              </Box>

              {selLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <>
                  <Grid container spacing={1.5}>
                    {allSessionImages.map((img) => {
                      const selected = selectedImageIds.includes(img.uniqueId);
                      return (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={img.uniqueId}>
                          <Card
                            onClick={() => !running && toggleImageSelection(img.uniqueId)}
                            sx={{
                              cursor: running ? 'default' : 'pointer',
                              border: selected ? '2px solid' : '2px solid transparent',
                              borderColor: selected ? 'primary.main' : 'transparent',
                              position: 'relative',
                              transition: 'border-color 0.15s',
                            }}
                          >
                            <AuthenticatedImage
                              imageId={img.uniqueId}
                              type="thumbnail"
                              fallbackToServe
                              alt={img.name}
                              sx={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
                            />
                            {selected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: 'primary.main',
                                  borderRadius: '50%',
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CheckCircle sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            )}
                            <CardContent sx={{ py: 0.5, px: 1 }}>
                              <Typography variant="caption" noWrap display="block">
                                {img.name}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <TablePagination
                    component="div"
                    count={selTotal}
                    page={selPage}
                    onPageChange={(_, p) => setSelPage(p)}
                    rowsPerPage={selRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setSelRowsPerPage(parseInt(e.target.value, 10));
                      setSelPage(0);
                    }}
                    rowsPerPageOptions={[12, 24, 48]}
                    sx={{ mt: 0.5 }}
                  />
                </>
              )}
            </Paper>
          )}

          {/* Stuck / 409 recovery alert */}
          {stuckOperation && !otherTabRunning && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mb: 2 }}
              action={
                <Button
                  color="warning"
                  size="small"
                  variant="outlined"
                  startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : <StopIcon />}
                  onClick={handleForceCancelAndRetry}
                  disabled={cancelling}
                >
                  {cancelling ? 'Clearing…' : 'Cancel & Retry'}
                </Button>
              }
            >
              A previous recognition run is still marked as <strong>in progress</strong> in the
              database. Click <strong>Cancel &amp; Retry</strong> to clear it and start a new run.
            </Alert>
          )}

          {/* Requirement warning */}
          {!hasPersons && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You must tag at least one reference person in the <strong>Tag Person</strong> tab
              before running recognition.
            </Alert>
          )}

          {/* ── Run / Cancel buttons ─────────────────────────────────────────── */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={running ? <CircularProgress size={18} color="inherit" /> : <RunIcon />}
              onClick={handleRunRecognition}
              disabled={running || otherTabRunning || !hasPersons || (useSubset && selectedImageIds.length === 0)}
            >
              {running
                ? 'Running Recognition…'
                : useSubset && selectedImageIds.length > 0
                ? `Find Faces in ${selectedImageIds.length} Selected Photo(s)`
                : 'Find Faces in All Photos'}
            </Button>

            {running && (
              <Button
                variant="outlined"
                color="error"
                size="large"
                startIcon={cancelling ? <CircularProgress size={18} color="inherit" /> : <StopIcon />}
                onClick={handleCancelRecognition}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling…' : 'Cancel'}
              </Button>
            )}
          </Stack>

          {/* ── Progress ─────────────────────────────────────────────────────── */}
          {runProgress && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                {/* Thumbnail of the image currently being processed */}
                {(() => {
                  const thumbId = runProgress.currentImageId
                    || allSessionImages.find(
                        (img) => img.name === runProgress.currentPhotoName || img.originalName === runProgress.currentPhotoName
                      )?.uniqueId;
                  return thumbId ? (
                    <Box sx={{ flexShrink: 0 }}>
                      <AuthenticatedImage
                        imageId={thumbId}
                        type="thumbnail"
                        fallbackToServe
                        alt={runProgress.currentPhotoName}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'grey.100',
                          display: 'block',
                        }}
                      />
                    </Box>
                  ) : null;
                })()}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '85%' }}>
                      {(() => {
                        const raw = runProgress.status || runProgress.statusMessage || 'Working…';
                        // Override stale 'Initialising…' once processing has actually started
                        if (
                          (raw === 'Initialising…' || raw === 'Initialising...') &&
                          runProgress.currentPhotoName
                        ) {
                          return 'Processing…';
                        }
                        return raw;
                      })()}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {runProgress.percentage ?? 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={runProgress.percentage ?? 0}
                    color={runProgress.hasError ? 'error' : runProgress.isCancelled ? 'warning' : runProgress.isCompleted ? 'success' : 'primary'}
                    sx={{ mb: 1 }}
                  />
                  {(() => {
                    // Use || (not ??) so that 0 falls through to the other field
                    const cur = runProgress.current || runProgress.currentIndex || 0;
                    const tot = runProgress.total || runProgress.totalCount || 0;
                    const photo = runProgress.currentPhotoName;
                    if (!tot && !photo) return null;
                    return (
                      <Typography variant="caption" color="text.secondary">
                        {tot > 0 ? `${cur} / ${tot} photos` : ''}
                        {tot > 0 && photo ? ` — ${photo}` : photo || ''}
                      </Typography>
                    );
                  })()}
                  {/* ── Timing metrics ──────────────────────────────────────── */}
                  {(runProgress.lastImageTime > 0 || runProgress.averageTime > 0 || runProgress.estimatedTimeRemaining > 0) && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {runProgress.lastImageTime > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Last image: <strong>{formatTime(runProgress.lastImageTime)}</strong>
                        </Typography>
                      )}
                      {runProgress.averageTime > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Avg/image: <strong>{formatTime(runProgress.averageTime)}</strong>
                        </Typography>
                      )}
                      {runProgress.estimatedTimeRemaining > 0 && !runProgress.isCompleted && !runProgress.isCancelled && (
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                          ETA: {formatTime(runProgress.estimatedTimeRemaining * 1000)}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {runProgress.isCompleted && !runProgress.hasError && !runProgress.isCancelled && (
                <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 1.5 }}>
                  Recognition complete. Switch to the <strong>Matches</strong> tab to review results.
                </Alert>
              )}
              {runProgress.isCancelled && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 1.5 }}>
                  Recognition was cancelled at {runProgress.percentage ?? 0}%. Partial results may be available in the Matches tab.
                </Alert>
              )}
              {runProgress.hasError && (
                <Alert severity="error" icon={<WarningIcon />} sx={{ mt: 1.5 }}>
                  {runProgress.error || 'Recognition encountered an error.'}
                </Alert>
              )}
            </Paper>
          )}

          {/* Sub-step guidance: show View Matches once a run completes */}
          {runCompleted && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => setTab(3)}>
                View Matches
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* ══ Tab 3 – Matches ═══════════════════════════════════════════════════ */}
      {tab === 3 && (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Expand a reference person to see all photos where they appear.
          </Typography>

          {loadingPersons ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : referencePersons.length === 0 ? (
            <Alert severity="info">No reference persons yet.</Alert>
          ) : (
            referencePersons.map((person) => {
              const primaryPhoto =
                person.referencePhotoDetails?.find((p) => p.isPrimary) ||
                person.referencePhotoDetails?.[0];
              const matches = personMatches[person._id];
              const isLoading = loadingMatches[person._id];

              return (
                <Accordion
                  key={person._id}
                  expanded={!!matches}
                  onChange={() => togglePersonMatches(person._id)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <AuthenticatedAvatar
                        imageId={primaryPhoto?.imageId || person.primaryImage?.imageId}
                        alt={person.personName}
                        sx={{ width: 44, height: 44 }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">{person.personName}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={`${person.stats?.totalReferencePhotos || 0} ref photos`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={`${person.stats?.imagesWithMatches || 0} matched images`}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : matches && matches.length === 0 ? (
                      <Alert severity="info">
                        No matches found yet — run "Find Faces" first.
                      </Alert>
                    ) : matches ? (
                      <Grid container spacing={1.5}>
                        {matches.map((match, idx) => (
                          <Grid item xs={6} sm={4} md={3} key={`${match.imageId}-${idx}`}>
                            <Card>
                              <AuthenticatedImage
                                imageId={match.imageId}
                                type="thumbnail"
                                fallbackToServe
                                alt={match.imageName || 'Match'}
                                sx={{ width: '100%', height: 120, objectFit: 'cover' }}
                              />
                              <CardContent sx={{ py: 0.5, px: 1 }}>
                                <Typography variant="caption" noWrap display="block">
                                  {match.imageName || 'Unknown'}
                                </Typography>
                                {match.confidence && (
                                  <Chip
                                    size="small"
                                    label={`${Math.round(match.confidence)}%`}
                                    color={match.confidence > 90 ? 'success' : 'default'}
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      )}

      {/* ── Divider + Navigation ──────────────────────────────────────────────── */}
      <Divider sx={{ mt: 4, mb: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        {/* Status summary */}
        <Box>
          {canContinue ? (
            <Alert severity={allAnalysed ? 'success' : 'info'} icon={<CheckCircle />} sx={{ py: 0.5 }}>
              {referencePersons.length} person{referencePersons.length !== 1 ? 's' : ''} tagged
              {allAnalysed
                ? ' — all images analysed. Ready to continue!'
                : runCompleted
                ? ' — recognition complete.'
                : '. Run “Find Faces” when ready, or continue.'}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Tag at least one person or skip with the button below.
            </Alert>
          )}
        </Box>

        {/* Navigation buttons */}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack}>
            Back
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<SkipIcon />}
            onClick={() => onNext({})}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => onNext({ referencePersonCount: referencePersons.length })}
            disabled={!canContinue}
            sx={{ minWidth: 180 }}
          >
            Continue
          </Button>
        </Stack>
      </Box>

      {/* ── Snackbar ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WizardStep2FaceRecognition;
